import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  IconButton,
  Select,
} from "@mui/material";

import { SiteUnit } from "./site.type";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteSiteUnitAction,
  getSiteUnitListAction,
  setAddSiteUnitResponse,
  setDeleteSiteUnitResponse,
  updateSiteUnitStatusAction,
  updateSiteUnitStatusResponse,
} from "../../../../store/reducers/storageSettingSlice";
import AlertPopup from "../../../../components/common/alertPopup/AlertPopup";
import TableDesign from "../../../../components/common/table/TableDesign";
import { STATUS_OPTIONS, TABLE_HEADINGS } from "./siteFields";
import { Lens } from "@mui/icons-material";
import { MoreVert as MoreIcon } from "@mui/icons-material";
import { setNotificationAction } from "../../../../store/reducers/mmymSlice";
import dayjs from "dayjs";
interface StorageUnitTableProps {
  selectedSiteId: string;
  floorNumber: number;
  onAssignUnit: (unitId: string) => void;
  onEditUnit: (unitId: string) => void;
  onDeleteUnit: (unitId: string) => void;
  showOnlyAvailable: boolean;
  selectedUnits?: string[];
  onSelectUnit?: (unitId: string) => void;
}

interface FilterOption {
  page: number;
  limit: number;
  orderBy: string;
  orderIn: string;
  siteId?: string | null;
  floorNumber?: number;
  isAvailable?: boolean;
}

const SiteUnitTable: React.FC<StorageUnitTableProps> = ({
  floorNumber,
  onAssignUnit,
  onEditUnit,
  onDeleteUnit,
  showOnlyAvailable,
  selectedUnits = [],
  onSelectUnit,
  selectedSiteId,
}) => {
  const dispatch = useDispatch();

  // Adjust this destructure to match your actual reducer shape.
  // If your API returns { data, total, totalPages }, expose those from the slice too.
  const {
    siteUnitList,
    addSiteUnitResponse,
    siteUnitPagination,
    isDeletedSiteUnitResponse,
    isSiteUnitStatusResponse,
  } = useSelector((state: any) => state.storageSetting);
const { userProfile } = useSelector((state: any) => state?.user);
  const [filterOption, setFilterOptions] = useState<FilterOption>({
    page: 1,
    limit: 10,
    orderBy: "",
    orderIn: "asc",
    siteId: null,
  });

  const [anchorEl, setAnchorEl] = useState(null);

  const openDown = Boolean(anchorEl);
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  // Reset to page 1 whenever site, floor, or the availability toggle changes
  useEffect(() => {
    if (selectedSiteId) {
      setFilterOptions((prev) => ({
        ...prev,
        siteId: selectedSiteId,
        floorNumber,
        isAvailable: showOnlyAvailable || undefined,
        page: 1,
      }));
    }
  }, [selectedSiteId, floorNumber, showOnlyAvailable]);

  // Fetch whenever filterOption changes (covers page changes too)
  useEffect(() => {
    if (filterOption.siteId || addSiteUnitResponse) {
      dispatch(getSiteUnitListAction(filterOption));
      dispatch(setAddSiteUnitResponse(false));
    }
  }, [filterOption, dispatch, addSiteUnitResponse]);

  useEffect(() => {
    if (isDeletedSiteUnitResponse || isSiteUnitStatusResponse) {
      dispatch(getSiteUnitListAction(filterOption));
      dispatch(setDeleteSiteUnitResponse(false));
      dispatch(updateSiteUnitStatusResponse(false));
    }
  }, [dispatch, filterOption, isDeletedSiteUnitResponse, isSiteUnitStatusResponse]);

  // Support either a raw array or a { data, total, totalPages } shape
  const units: SiteUnit[] = Array.isArray(siteUnitList)
    ? siteUnitList
    : siteUnitList?.data || [];

  const handleEdit = () => {
    setAnchorEl(null);
    if (selectedUnitId) {
      onEditUnit(selectedUnitId);
    }
  };

  const handleDeleteClick = () => {
    setAnchorEl(null);
    if (selectedUnitId) {
      setDeleteAlertOpen(true);
    }
  };

  const confirmDelete = () => {
    if (!selectedUnitId || !selectedSiteId) {
      setDeleteAlertOpen(false);
      return;
    }

    dispatch(
      deleteSiteUnitAction({
        siteId: selectedSiteId,
        siteUnitId: selectedUnitId,
      }),
    );
    setDeleteAlertOpen(false);
    setSelectedUnitId(null);
  };

  const handleClose = () => {
    setDeleteAlertOpen(false);
    setSelectedUnitId(null);

    dispatch(setNotificationAction({ message: [], severity: "info" }));
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          bgcolor: "#fff",
          height: {
            xs: "auto",
            sm: units?.length > 2 ? "100vh" : "40vh",
          },
          border: "1px solid #d1cfcf",
          borderRadius: "5px",
          padding: "0px",
          overflow: "hidden",
        }}
      >
        <TableDesign
          tableHeadings={TABLE_HEADINGS}
          tableHeadColor={"#02906f"}
          cellColor={"#fff"}
          pagination={units?.length > 0 ? siteUnitPagination : null}
          isLoader={false}
          stickyHeader={true}
          filterOption={filterOption}
          setFilterOption={setFilterOptions}
          // extraCSS={{
          //   height: "calc(100%)",
          // }}
        >
          {units?.length > 0 ? (
            units?.map((val: any, idx: number) => (
              <TableRow
                key={val?.id}
                sx={{
                  cursor: "pointer",
                  marginTop: "10px",
                  padding: "4px !important",
                  borderRadius: "20px !important",
                  height: "20px !important",
                  // "&:hover": {
                  //   backgroundColor: "#f0f0f0",
                  //   outline: "2px solid #f0f0f0",
                  //   borderRadius: "0px !important",
                  //   outlineOffset: "-2px !important",
                  // },
                }}
              >
                <TableCell>{val?.unit_ref_id}</TableCell>
                <TableCell>{val?.size_ft3 ?? "-"}</TableCell>
                <TableCell>{val?.rent_type}</TableCell>
                <TableCell>{val?.price_per_unit ? `£ ${val?.price_per_unit}` : ''}</TableCell>
                <TableCell>{val?.out_date ? dayjs(val?.out_date).format(userProfile?.dateFormat ?? "DD/MM/YYYY") : ''}</TableCell>
                {/* <TableCell></TableCell> */}
                <TableCell>
                  <Select
                    value={val?.status}
                    size="small"
                    variant="standard"
                    disableUnderline
                    disabled={val?.status?.toLowerCase() === "occupied"}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      dispatch(
                        updateSiteUnitStatusAction({
                          siteId: selectedSiteId,
                          siteUnitId: val?.id,
                          status: newStatus,
                        }),
                      );
                    }}
                    renderValue={(selected) => {
                      const opt = STATUS_OPTIONS.find(
                        (o) => o.value === selected,
                      );
                      return (
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "10px",
                            color: opt?.color ?? "#22A06B",
                            whiteSpace: "nowrap",
                            textTransform:'uppercase'
                          }}
                        >
                          {opt?.label ?? selected}
                        </Typography>
                      );
                    }}
                    sx={{
                      minWidth: 130,
                      borderRadius: "20px",
                      backgroundColor:
                        STATUS_OPTIONS.find((o) => o.value?.toLocaleLowerCase() === val?.status?.toLocaleLowerCase())
                          ?.bg ?? "#E9F7EF",
                      "& .MuiSelect-select": {
                        padding: "4px 14px",
                        display: "flex",
                        alignItems: "center",
                        borderRadius: "20px",
                      },
                      "& .MuiSelect-icon": {
                        color:
                          STATUS_OPTIONS.find((o) => o.value?.toLocaleLowerCase() === val?.status?.toLocaleLowerCase())
                            ?.color ?? "#22A06B",
                        right: "10px",
                      },
                      // "&:hover": {
                      //   backgroundColor:
                      //     STATUS_OPTIONS.find((o) => o.value === val?.status)
                      //       ?.bg ?? "#E9F7EF",
                      // },
                    }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "13px",
                            color: opt.color ?? "#22A06B",
                          }}
                        >
                          {opt.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    id="basic-button"
                    aria-controls={openDown ? "basic-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={openDown ? "true" : undefined}
                    onClick={(e) => {
                      handleClick(e);
                      setSelectedUnitId(val?.id);
                    }}
                  >
                    <MoreIcon />
                  </IconButton>
                  <Menu
                    sx={{
                      boxShadow: "none",
                      border: "2px solid #eeecef",
                      "& .MuiPaper-root": {
                        boxShadow: "none",
                        border: "2px solid #eeecef",
                      },
                      top: "0px",
                    }}
                    // className={classes.menuBox}
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={openDown}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "basic-button",
                    }}
                  >
                    <MenuItem onClick={() => handleEdit()}>
                      <Lens
                        style={{
                          height: "4px",
                          width: "4px",
                          marginRight: "4px",
                        }}
                      />
                      Edit
                    </MenuItem>

                    <MenuItem onClick={() => handleDeleteClick()}>
                      <Lens
                        style={{
                          height: "4px",
                          width: "4px",
                          marginRight: "4px",
                        }}
                      />
                      Delete
                    </MenuItem>
                  </Menu>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={TABLE_HEADINGS?.length} align="center">
                <Box
                  sx={{
                    height: "60vh",
                    display: "flex",
                    // alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      fontSize: "1rem",
                      color: "#4A4A4A",
                      top: "100px",
                    }}
                  >
                    {`No Data Found!`}
                  </span>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableDesign>
      </Box>

      {deleteAlertOpen && (
        <AlertPopup
          open={deleteAlertOpen}
          title="Delete Unit"
          content="Are you sure you want to delete this unit?"
          onHide={() => setDeleteAlertOpen(false)}
          submitHandler={confirmDelete}
          yesText="Yes"
          noText="No"
        />
      )}
    </>
  );
};

export default SiteUnitTable;
